import { Spinner } from "@via/components/loading/loading.tsx";
import ToggleButton from "@via/components/toggle-button.tsx";
import { type PanelSubmitHandle } from "@via/pages/account/admin/organizations/panels/general-panel.tsx";
import {
  useGetOrganizationFeatureFlags,
  useReplaceOrganizationFeatureFlags,
} from "@via/pages/account/admin/organizations/organizations.api.tsx";
import {
  AdminOrganizationEntitlement,
  GetUserOrganizationDetailsForAdminResponseBodyItem,
} from "common";
import { forwardRef, useImperativeHandle, useState } from "react";

/**
 * The Entitlements tab: the whole flag catalog, with toggles only where a grant can actually be
 * made or removed. The tab is named Entitlements rather than Feature Flags because the grant is
 * membership derived: it follows the member everywhere, it does not stop at this organization.
 *
 * Eligibility is enforced here exactly as the server enforces it on the diff (spec 7.8): an
 * ungranted row that is not organization grantable, or is enabled globally, cannot be toggled on
 * (the server would reject the whole request); a granted row can ALWAYS be toggled off, including
 * an inert drift grant, because removal is always allowed. The save sends the full granted list;
 * unchanged ineligible grants are preserved server side, so resending them is safe.
 */
const EntitlementsPanel = forwardRef<
  PanelSubmitHandle,
  { organization: GetUserOrganizationDetailsForAdminResponseBodyItem }
>(({ organization }, ref) => {
  const { data, isLoading, isError } = useGetOrganizationFeatureFlags(
    { organizationId: organization.id },
    true
  );
  const { mutateAsync: replaceEntitlements } =
    useReplaceOrganizationFeatureFlags();
  const [changedGrants, setChangedGrants] = useState<Record<string, boolean>>(
    {}
  );

  const isGranted = (entitlement: AdminOrganizationEntitlement): boolean =>
    entitlement.envVarName in changedGrants
      ? changedGrants[entitlement.envVarName]
      : entitlement.granted;

  const canToggle = (entitlement: AdminOrganizationEntitlement): boolean =>
    isGranted(entitlement) ||
    (entitlement.organizationGrantable && !entitlement.globallyEnabled);

  const caption = (
    entitlement: AdminOrganizationEntitlement
  ): string | null => {
    if (entitlement.granted && entitlement.inertReason === "not_grantable") {
      return "Granted, currently inert: not organization grantable";
    }
    if (!entitlement.granted && !entitlement.organizationGrantable) {
      return "Not organization grantable";
    }
    if (!entitlement.granted && entitlement.globallyEnabled) {
      return "Enabled globally; an organization grant would only take effect if the global setting were withdrawn";
    }
    return null;
  };

  const submit = async (): Promise<boolean> => {
    if (Object.keys(changedGrants).length === 0) {
      return true;
    }
    const featureFlags = (data?.entitlements ?? [])
      .filter(isGranted)
      .map(entitlement => entitlement.envVarName);
    try {
      await replaceEntitlements({
        organizationId: organization.id,
        featureFlags,
      });
      setChangedGrants({});
      return true;
    } catch {
      // The mutation's own error notification carries the server's reason; staying open lets
      // the operator correct the form instead of losing it.
      return false;
    }
  };

  useImperativeHandle(ref, () => ({ submit }));

  if (isLoading) {
    return <Spinner loading className="mx-auto my-8 h-6 w-6" />;
  }
  if (isError || !data) {
    return (
      <p className="pb-5 pt-2 text-sm text-gray-500">
        The entitlement catalog could not be loaded.
      </p>
    );
  }

  return (
    <div>
      <p
        data-testid="entitlements-contract"
        className="pb-4 text-sm text-gray-600">
        Granting a feature to this organization turns it on for every current
        and future member, everywhere that member works, including their
        personal resources and any other organization they belong to.
      </p>
      <ul className="divide-y divide-gray-100">
        {data.entitlements.map(entitlement => (
          <li
            key={entitlement.envVarName}
            data-testid={`entitlement-row-${entitlement.envVarName}`}
            className="flex items-start justify-between gap-4 py-3">
            <div>
              <p className="text-sm font-medium">{entitlement.name}</p>
              {entitlement.description && (
                <p className="text-sm text-gray-500">
                  {entitlement.description}
                </p>
              )}
              {caption(entitlement) && (
                <p
                  data-testid={`entitlement-caption-${entitlement.envVarName}`}
                  className="text-xs text-amber-700">
                  {caption(entitlement)}
                </p>
              )}
            </div>
            <ToggleButton
              isOn={isGranted(entitlement)}
              onToggle={() =>
                setChangedGrants(previous => ({
                  ...previous,
                  [entitlement.envVarName]: !isGranted(entitlement),
                }))
              }
              disabled={!canToggle(entitlement)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
});

EntitlementsPanel.displayName = "EntitlementsPanel";

export default EntitlementsPanel;
